/**
 * FERP Enhanced PDF Toolbar (DRR-079)
 *
 * Full-featured toolbar with:
 * - Page navigation
 * - Zoom (including fit-width, fit-page)
 * - View mode (single, continuous, spread)
 * - All annotation tools (highlight, text, stamp, ink, shape, sticky, redaction)
 * - Undo/redo
 * - Panel toggles
 * - Search
 * - Sign/Export actions
 *
 * FERP Constraints: Mantine v8.x ONLY
 */

import {
  Group,
  ActionIcon,
  Text,
  TextInput,
  Tooltip,
  Divider,
  Select,
  Paper,
  Menu,
  ColorSwatch,
} from '@mantine/core';
import {
  IconChevronLeft,
  IconChevronRight,
  IconZoomIn,
  IconZoomOut,
  IconArrowsMaximize,
  IconArrowAutofitWidth,
  IconHighlight,
  IconMessage,
  IconRubberStamp,
  IconPointer,
  IconPencil,
  IconSquare,
  IconNote,
  IconEyeOff,
  IconArrowBackUp,
  IconArrowForwardUp,
  IconSignature,
  IconDownload,
  IconSearch,
  IconLayoutSidebar,
  IconLayoutSidebarRight,
  IconColumns,
  IconPresentation,
  IconFile,
  IconMaximize,
  IconMinimize,
  IconLock,
} from '@tabler/icons-react';
import { usePdfSuite } from './PdfSuiteContext';
import type { StampValue, ShapeType, ViewMode } from './types';

interface EnhancedPdfToolbarProps {
  canAnnotate?: boolean;
  canSign?: boolean;
  onRequestSign?: () => void;
  onRequestExport?: () => void;
}

const INK_COLORS = ['#000000', '#FF0000', '#0066FF', '#00AA00', '#FF6600', '#9900CC'];

export function EnhancedPdfToolbar({
  canAnnotate = false,
  canSign = false,
  onRequestSign,
  onRequestExport,
}: EnhancedPdfToolbarProps) {
  const {
    state,
    dispatch,
    setCurrentPage,
    setScale,
    setAnnotationMode,
    setViewMode,
    undo,
    redo,
    canUndo,
    canRedo,
    toggleFullscreen,
    isPro,
  } = usePdfSuite();

  const { viewer } = state;
  const { numPages, currentPage, scale, annotationMode, viewMode, searchQuery } = viewer;

  const zoomIn = () => setScale(scale + 0.25);
  const zoomOut = () => setScale(scale - 0.25);
  const fitWidth = () => setScale(1.0, 'fit-width');
  const fitPage = () => setScale(0.75, 'fit-page');

  const toggleMode = (mode: typeof annotationMode) => {
    setAnnotationMode(annotationMode === mode ? null : mode);
  };

  const modeBtn = (mode: typeof annotationMode, icon: React.ReactNode, label: string, color?: string) => (
    <Tooltip label={label} key={label}>
      <ActionIcon
        variant={annotationMode === mode ? 'filled' : 'subtle'}
        color={color}
        size="sm"
        onClick={() => toggleMode(mode)}
      >
        {icon}
      </ActionIcon>
    </Tooltip>
  );

  const viewModeIcon: Record<ViewMode, React.ReactNode> = {
    single: <IconFile size={14} />,
    continuous: <IconFile size={14} />,
    spread: <IconColumns size={14} />,
    presentation: <IconPresentation size={14} />,
  };

  return (
    <Paper
      withBorder
      p="xs"
      style={{ borderBottom: 'none', borderRadius: '4px 4px 0 0' }}
    >
      <Group gap="xs" wrap="nowrap">
        {/* Page Navigation */}
        <Group gap={4}>
          <Tooltip label="Previous page">
            <ActionIcon
              variant="subtle"
              size="sm"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage(currentPage - 1)}
            >
              <IconChevronLeft size={16} />
            </ActionIcon>
          </Tooltip>
          <Text size="sm" fw={500}>
            {currentPage} / {numPages || '?'}
          </Text>
          <Tooltip label="Next page">
            <ActionIcon
              variant="subtle"
              size="sm"
              disabled={currentPage >= numPages}
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              <IconChevronRight size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>

        <Divider orientation="vertical" />

        {/* Zoom Controls */}
        <Group gap={4}>
          <Tooltip label="Zoom out">
            <ActionIcon variant="subtle" size="sm" onClick={zoomOut}>
              <IconZoomOut size={16} />
            </ActionIcon>
          </Tooltip>
          <Text size="xs" w={45} ta="center">
            {Math.round(scale * 100)}%
          </Text>
          <Tooltip label="Zoom in">
            <ActionIcon variant="subtle" size="sm" onClick={zoomIn}>
              <IconZoomIn size={16} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Fit width">
            <ActionIcon variant="subtle" size="sm" onClick={fitWidth}>
              <IconArrowAutofitWidth size={16} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Fit page">
            <ActionIcon variant="subtle" size="sm" onClick={fitPage}>
              <IconArrowsMaximize size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>

        <Divider orientation="vertical" />

        {/* View Mode */}
        <Menu shadow="sm" width={140}>
          <Menu.Target>
            <Tooltip label={`View: ${viewMode}`}>
              <ActionIcon variant="subtle" size="sm">
                {viewModeIcon[viewMode]}
              </ActionIcon>
            </Tooltip>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item leftSection={<IconFile size={14} />} onClick={() => setViewMode('single')}>
              Single Page
            </Menu.Item>
            <Menu.Item leftSection={<IconFile size={14} />} onClick={() => setViewMode('continuous')}>
              Continuous
            </Menu.Item>
            {isPro && (
              <Menu.Item leftSection={<IconColumns size={14} />} onClick={() => setViewMode('spread')}>
                Spread
              </Menu.Item>
            )}
            <Menu.Item leftSection={<IconPresentation size={14} />} onClick={() => setViewMode('presentation')}>
              Presentation
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>

        {/* Fullscreen toggle */}
        <Tooltip label={state.isFullscreen ? 'Exit fullscreen (Esc)' : 'Fullscreen'}>
          <ActionIcon
            variant={state.isFullscreen ? 'filled' : 'subtle'}
            size="sm"
            onClick={toggleFullscreen}
          >
            {state.isFullscreen ? <IconMinimize size={16} /> : <IconMaximize size={16} />}
          </ActionIcon>
        </Tooltip>

        <Divider orientation="vertical" />

        {/* Annotation Tools */}
        {canAnnotate && (
          <>
            <Group gap={4}>
              {modeBtn(null, <IconPointer size={16} />, 'Select')}
              {/* Free tier: highlight + text */}
              {modeBtn('highlight', <IconHighlight size={16} />, 'Highlight', 'yellow')}
              {modeBtn('text', <IconMessage size={16} />, 'Text comment', 'orange')}
              {/* Pro tier: stamps, ink, shapes, sticky, redaction */}
              {isPro && modeBtn('stamp', <IconRubberStamp size={16} />, 'Stamp', 'green')}
              {isPro && modeBtn('ink', <IconPencil size={16} />, 'Freehand draw', 'blue')}
              {isPro && modeBtn('shape', <IconSquare size={16} />, 'Shape', 'violet')}
              {isPro && modeBtn('sticky', <IconNote size={16} />, 'Sticky note', 'yellow')}
              {isPro && modeBtn('redaction', <IconEyeOff size={16} />, 'Redaction mark', 'red')}
              {!isPro && (
                <Tooltip label="Upgrade for advanced tools">
                  <ActionIcon variant="subtle" size="sm" disabled>
                    <IconLock size={14} />
                  </ActionIcon>
                </Tooltip>
              )}
            </Group>

            {/* Stamp selector (pro) */}
            {isPro && annotationMode === 'stamp' && (
              <Select
                size="xs"
                w={120}
                data={[
                  { value: 'APPROVED', label: 'Approved' },
                  { value: 'REJECTED', label: 'Rejected' },
                  { value: 'REVIEWED', label: 'Reviewed' },
                ]}
                value={viewer.selectedStamp}
                onChange={(v) =>
                  v && dispatch({ type: 'SET_SELECTED_STAMP', stamp: v as StampValue })
                }
                allowDeselect={false}
              />
            )}

            {/* Shape selector (pro) */}
            {isPro && annotationMode === 'shape' && (
              <Select
                size="xs"
                w={120}
                data={[
                  { value: 'rectangle', label: 'Rectangle' },
                  { value: 'circle', label: 'Circle' },
                  { value: 'arrow', label: 'Arrow' },
                  { value: 'line', label: 'Line' },
                ]}
                value={viewer.selectedShape}
                onChange={(v) =>
                  v && dispatch({ type: 'SET_SELECTED_SHAPE', shape: v as ShapeType })
                }
                allowDeselect={false}
              />
            )}

            {/* Ink color picker (pro) */}
            {isPro && annotationMode === 'ink' && (
              <Group gap={4}>
                {INK_COLORS.map((color) => (
                  <Tooltip label={color} key={color}>
                    <ColorSwatch
                      color={color}
                      size={18}
                      style={{
                        cursor: 'pointer',
                        outline:
                          viewer.inkColor === color
                            ? '2px solid var(--mantine-color-blue-5)'
                            : 'none',
                        outlineOffset: 1,
                      }}
                      onClick={() => dispatch({ type: 'SET_INK_COLOR', color })}
                    />
                  </Tooltip>
                ))}
              </Group>
            )}

            <Divider orientation="vertical" />

            {/* Undo/Redo (pro) */}
            {isPro && (
              <Group gap={4}>
                <Tooltip label="Undo (Ctrl+Z)">
                  <ActionIcon variant="subtle" size="sm" disabled={!canUndo} onClick={undo}>
                    <IconArrowBackUp size={16} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Redo (Ctrl+Shift+Z)">
                  <ActionIcon variant="subtle" size="sm" disabled={!canRedo} onClick={redo}>
                    <IconArrowForwardUp size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            )}

            <Divider orientation="vertical" />
          </>
        )}

        {/* Search */}
        <TextInput
          size="xs"
          placeholder="Search text..."
          leftSection={<IconSearch size={14} />}
          value={searchQuery}
          onChange={(e) =>
            dispatch({ type: 'SET_SEARCH_QUERY', query: e.currentTarget.value })
          }
          w={160}
        />

        <div style={{ flex: 1 }} />

        {/* Panel Toggles (pro only) */}
        {isPro && (
          <Group gap={4}>
            <Tooltip label="Toggle left panel">
              <ActionIcon
                variant={state.leftPanel !== null ? 'light' : 'subtle'}
                size="sm"
                onClick={() =>
                  dispatch({
                    type: 'SET_LEFT_PANEL',
                    tab: state.leftPanel !== null ? null : 'thumbnails',
                  })
                }
              >
                <IconLayoutSidebar size={16} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Toggle right panel">
              <ActionIcon
                variant={state.rightPanel !== null ? 'light' : 'subtle'}
                size="sm"
                onClick={() =>
                  dispatch({
                    type: 'SET_RIGHT_PANEL',
                    tab: state.rightPanel !== null ? null : (state.documentTrailMeta ? 'trail' : 'annotations'),
                  })
                }
              >
                <IconLayoutSidebarRight size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
        )}

        <Divider orientation="vertical" />

        {/* Actions (sign + export are pro) */}
        <Group gap={4}>
          {isPro && canSign && onRequestSign && (
            <Tooltip label="Sign document">
              <ActionIcon variant="light" color="violet" size="sm" onClick={onRequestSign}>
                <IconSignature size={16} />
              </ActionIcon>
            </Tooltip>
          )}
          {isPro && onRequestExport && (
            <Tooltip label="Export PDF with annotations">
              <ActionIcon variant="light" size="sm" onClick={onRequestExport}>
                <IconDownload size={16} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
      </Group>
    </Paper>
  );
}
